import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  Divider,
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AddIcon from "@mui/icons-material/Add";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DataContext from "./context";
import { useData, useDataManager } from "./useDashboard";
import Loader from "./loader";
import { formatValue, moneyMask } from "../../../functions";
import { useNavigate } from "react-router-dom";

const theme = createTheme({
  palette: {
    primary: { main: "#fbb016" },
    secondary: { main: "#002f87" },
    background: { default: "#f4f6f8" },
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
    h4: { fontWeight: 800 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 16 },
});

const AdminDashboard = () => {
  const { dashboard } = useData();
  const navigate = useNavigate();

  // Dados simulados para os cards
  const stats = [
    {
      label: "Clientes Totais",
      value: dashboard.totalCustomers,
      icon: <PeopleAltOutlinedIcon />,
      color: "#002f87",
    },
    {
      label: "Brindes Ativos",
      value: dashboard.totalRewards,
      icon: <CardGiftcardOutlinedIcon />,
      color: "#fbb016",
    },
    {
      label: "Resgates",
      value: dashboard.totalRedeemed,
      icon: <TrendingUpIcon />,
      color: "#2e7d32",
    },
    {
      label: "Vendas Vinculadas",
      value: formatValue(dashboard.totalSalesAmount),
      icon: <ShoppingCartOutlinedIcon />,
      color: "#ed6c02",
    },
  ];

  return (
    <React.Fragment>
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: { overflowY: "auto", scrollBehavior: "smooth" },
          body: { backgroundColor: "#f4f6f8" },
        }}
      />

      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {/* BOAS VINDAS E AÇÕES RÁPIDAS */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 4 }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4">Dashboard</Typography>
              <Typography variant="body2" color="text.secondary">
                Bem-vindo de volta!
              </Typography>
            </Box>
          </Stack>

          {/* GRID DE MÉTRICAS (INDICADORES) */}
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {stats.map((stat, i) => (
              <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Paper
                  elevation={0}
                  sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 4 }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: `${stat.color}15`,
                        color: stat.color,
                        display: "flex",
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h5" fontWeight="900">
                        {stat.value}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {/* ÚLTIMOS CLIENTES CADASTRADOS */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e0e0e0", height: "100%" }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Clientes Recentes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {dashboard.lastCustomers.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "#eee",
                          borderRadius: "50%",
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="700">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cadastrado em{" "}
                          {new Date(item.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color="secondary"
                    >
                      {item.wallet.balance} pts
                    </Typography>
                  </Box>
                ))}
                <Button
                  onClick={() => navigate("./customers")}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Ver todos os clientes
                </Button>
              </Paper>
            </Grid>

            {/* BRINDES MAIS PROCURADOS / ALERTAS */}
            {/* <Grid item size={{ xs: 12, md: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px solid #e0e0e0",
                  bgcolor: "secondary.main",
                  color: "white",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Destaques da Semana
                </Typography>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption">
                      BRINDE MAIS RESGATADO
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      Kit Trincha Premium (12)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption">ALERTA DE ESTOQUE</Typography>
                    <Typography variant="body1" fontWeight="700">
                      Bonés Fideliza (2 restantes)
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid> */}
          </Grid>
        </Container>
      </Box>
    </React.Fragment>
  );
};

const Index = () => {
  const { loading } = useData();

  return loading ? <Loader /> : <AdminDashboard />;
};
const Provider = () => {
  return (
    <DataContext.Provider value={useDataManager()}>
      <Index />
    </DataContext.Provider>
  );
};

export default Provider;
